import { adminApi, parseApiError } from './api';
import type { PlanWithLimits, CreatePlanDto, UpdatePlanDto, StripeProduct, PlanSyncMapping, CreateStripeProductDto } from '@/types/admin.types';

// API response wrappers
interface ApiResponse<T> {
  success: boolean;
  [key: string]: T | boolean;
}

export const plansService = {
  async getAll(): Promise<PlanWithLimits[]> {
    try {
      const { data } = await adminApi.get<ApiResponse<PlanWithLimits[]> & { plans: PlanWithLimits[] }>('/admin/plans');
      return data?.plans || [];
    } catch (error) {
      const err = parseApiError(error);
      if (err.message?.includes('404')) {
        return [];
      }
      throw err;
    }
  },

  async getById(id: string): Promise<PlanWithLimits | null> {
    try {
      const { data } = await adminApi.get<ApiResponse<PlanWithLimits> & { plan: PlanWithLimits }>(`/admin/plans/${id}`);
      return data?.plan || null;
    } catch (error) {
      const err = parseApiError(error);
      if (err.message?.includes('404')) {
        return null;
      }
      throw err;
    }
  },

  async create(dto: CreatePlanDto): Promise<PlanWithLimits> {
    try {
      const { data } = await adminApi.post<ApiResponse<PlanWithLimits> & { plan: PlanWithLimits }>('/admin/plans', dto);
      return data?.plan || data as unknown as PlanWithLimits;
    } catch (error) {
      throw parseApiError(error);
    }
  },

  async update(id: string, dto: UpdatePlanDto): Promise<PlanWithLimits> {
    try {
      const { data } = await adminApi.put<ApiResponse<PlanWithLimits> & { plan: PlanWithLimits }>(`/admin/plans/${id}`, dto);
      return data?.plan || data as unknown as PlanWithLimits;
    } catch (error) {
      throw parseApiError(error);
    }
  },

  async delete(id: string): Promise<void> {
    try {
      await adminApi.delete(`/admin/plans/${id}`);
    } catch (error) {
      throw parseApiError(error);
    }
  },

  // Stripe sync endpoints
  async getStripeProducts(): Promise<StripeProduct[]> {
    try {
      const { data } = await adminApi.get<ApiResponse<StripeProduct[]> & { products: StripeProduct[] }>('/admin/plans/stripe/products');
      return data?.products || [];
    } catch (error) {
      const err = parseApiError(error);
      if (err.message?.includes('404') || err.message?.includes('Stripe')) {
        return [];
      }
      throw err;
    }
  },

  async syncStripeProduct(mapping: PlanSyncMapping): Promise<PlanWithLimits> {
    try {
      const { data } = await adminApi.post<ApiResponse<PlanWithLimits> & { plan: PlanWithLimits }>('/admin/plans/stripe/sync', mapping);
      return data?.plan || data as unknown as PlanWithLimits;
    } catch (error) {
      throw parseApiError(error);
    }
  },

  async syncAllStripeProducts(mappings: PlanSyncMapping[]): Promise<{ synced: number; plans: PlanWithLimits[] }> {
    try {
      const { data } = await adminApi.post<{ success: boolean; synced: number; plans: PlanWithLimits[] }>(
        '/admin/plans/stripe/sync-all',
        { mappings }
      );
      return { synced: data?.synced || 0, plans: data?.plans || [] };
    } catch (error) {
      throw parseApiError(error);
    }
  },

  async createStripeProduct(dto: CreateStripeProductDto): Promise<{ plan: PlanWithLimits; stripeProductId: string }> {
    try {
      const { data } = await adminApi.post<{ success: boolean; plan: PlanWithLimits; stripeProductId: string }>(
        '/admin/plans/stripe/create',
        dto
      );
      return { plan: data?.plan, stripeProductId: data?.stripeProductId };
    } catch (error) {
      throw parseApiError(error);
    }
  },
};

export default plansService;
